// src/components/AnnoncesManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  collectionGroup,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { PlusCircle, Edit, Trash2, Loader2, Info, XCircle, CheckCircle } from 'lucide-react';

const AnnoncesManagement = ({ db, userRole, schoolId }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAnnouncementTitle, setNewAnnouncementTitle] = useState('');
  const [newAnnouncementContent, setNewAnnouncementContent] = useState('');
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingContent, setEditingContent] = useState('');

  const [localMessage, setLocalMessage] = useState({ type: '', text: '' });
  const [localError, setLocalError] = useState('');

  const isAdminOrHigher = userRole === 'admin' || userRole === 'superAdmin';

  const clearMessages = () => {
    setTimeout(() => {
      setLocalMessage({ type: '', text: '' });
      setLocalError('');
    }, 5000);
  };

  useEffect(() => {
    if (!db) {
      setLocalError('Base de données Firestore non disponible.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLocalError('');
    setLocalMessage({ type: '', text: '' });

    let announcementsQuery;
    if (userRole === 'superAdmin') {
      announcementsQuery = query(collectionGroup(db, 'announcements'), orderBy('createdAt', 'desc'));
    } else if (schoolId) {
      announcementsQuery = query(collection(db, 'schools', schoolId, 'announcements'), orderBy('createdAt', 'desc'));
    } else {
      setLocalError('ID d\'école manquant pour charger les annonces.');
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(announcementsQuery, (snapshot) => {
      const announcementsList = [];
      snapshot.forEach((doc) => {
        announcementsList.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate().toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) || 'N/A',
        });
      });
      setAnnouncements(announcementsList);
      setLoading(false);
    }, (error) => {
      console.error("Erreur lors du chargement des annonces: ", error);
      setLocalError(`Erreur lors du chargement des annonces. Vérifiez vos permissions Firestore et les index. ${error.message}`);
      setLoading(false);
      clearMessages();
    });

    return () => unsubscribe();
  }, [db, userRole, schoolId]);

  const handleAddAnnouncement = async () => {
    if (!newAnnouncementTitle.trim() || !newAnnouncementContent.trim()) {
      setLocalError('Veuillez remplir tous les champs pour la nouvelle annonce.');
      clearMessages();
      return;
    }
    if (!schoolId) {
      setLocalError('Impossible d\'ajouter une annonce: ID d\'école manquant. Veuillez sélectionner une école.');
      clearMessages();
      return;
    }

    setLoading(true);
    setLocalError('');
    setLocalMessage({ type: '', text: '' });
    try {
      await addDoc(collection(db, 'schools', schoolId, 'announcements'), {
        title: newAnnouncementTitle,
        content: newAnnouncementContent,
        createdAt: serverTimestamp(),
        schoolId: schoolId,
        createdBy: userRole,
      });
      setLocalMessage({ type: 'success', text: 'Annonce ajoutée avec succès !' });
      setNewAnnouncementTitle('');
      setNewAnnouncementContent('');
      clearMessages();
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'annonce: ", error);
      setLocalError(`Erreur lors de l'ajout de l'annonce : ${error.message}`);
      clearMessages();
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (announcement) => {
    setEditingAnnouncement(announcement);
    setEditingTitle(announcement.title);
    setEditingContent(announcement.content);
    setLocalError('');
    setLocalMessage({ type: '', text: '' });
  };

  const handleUpdateAnnouncement = async () => {
    if (!editingTitle.trim() || !editingContent.trim()) {
      setLocalError('Veuillez remplir tous les champs pour modifier l\'annonce.');
      clearMessages();
      return;
    }
    if (!editingAnnouncement || !schoolId) {
      setLocalError('Impossible de modifier l\'annonce: données manquantes ou ID d\'école.');
      clearMessages();
      return;
    }

    setLoading(true);
    setLocalError('');
    setLocalMessage({ type: '', text: '' });
    try {
      const docRef = doc(db, 'schools', schoolId, 'announcements', editingAnnouncement.id);
      await updateDoc(docRef, {
        title: editingTitle,
        content: editingContent,
        updatedAt: serverTimestamp(),
      });
      setLocalMessage({ type: 'success', text: 'Annonce mise à jour avec succès !' });
      setEditingAnnouncement(null);
      setEditingTitle('');
      setEditingContent('');
      clearMessages();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'annonce: ", error);
      setLocalError(`Erreur lors de la mise à jour de l'annonce : ${error.message}`);
      clearMessages();
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
      return;
    }
    if (!schoolId) {
      setLocalError('Impossible de supprimer l\'annonce: ID d\'école manquant.');
      clearMessages();
      return;
    }

    setLoading(true);
    setLocalError('');
    setLocalMessage({ type: '', text: '' });
    try {
      await deleteDoc(doc(db, 'schools', schoolId, 'announcements', announcementId));
      setLocalMessage({ type: 'success', text: 'Annonce supprimée avec succès !' });
      clearMessages();
    } catch (error) {
      console.error("Erreur lors de la suppression de l'annonce: ", error);
      setLocalError(`Erreur lors de la suppression de l'annonce : ${error.message}`);
      clearMessages();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-purple-50 rounded-lg border border-purple-200 shadow-md">
      <h3 className="text-2xl font-bold text-purple-800 mb-6 flex items-center">
        <Info size={24} className="mr-2" /> Gestion des Annonces
      </h3>

      {loading && (
        <div className="flex items-center justify-center text-blue-500 mb-4">
          <Loader2 className="animate-spin mr-2" size={20} /> Chargement...
        </div>
      )}
      {localMessage.text && (
        <div className={`flex items-center p-3 mb-4 rounded-md ${localMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
          {localMessage.type === 'success' ? <CheckCircle size={20} className="mr-2" /> : <Info size={20} className="mr-2" />}
          {localMessage.text}
        </div>
      )}
      {localError && (
        <div className="flex items-center p-3 mb-4 rounded-md bg-red-100 text-red-700">
          <XCircle size={20} className="mr-2" /> {localError}
        </div>
      )}

      {(isAdminOrHigher || userRole === 'gestionnaire') && (
        <div className="mb-8 p-4 border border-purple-300 rounded-lg bg-white shadow-sm">
          <h4 className="text-xl font-semibold text-purple-700 mb-3">Ajouter une nouvelle annonce</h4>
          <input
            type="text"
            placeholder="Titre de l'annonce"
            value={newAnnouncementTitle}
            onChange={(e) => setNewAnnouncementTitle(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md mb-3 focus:ring-purple-500 focus:border-purple-500"
          />
          <textarea
            placeholder="Contenu de l'annonce"
            value={newAnnouncementContent}
            onChange={(e) => setNewAnnouncementContent(e.target.value)}
            rows="4"
            className="w-full p-2 border border-gray-300 rounded-md mb-3 focus:ring-purple-500 focus:border-purple-500"
          ></textarea>
          <button
            onClick={handleAddAnnouncement}
            className="bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition duration-200 flex items-center"
            disabled={loading}
          >
            <PlusCircle size={18} className="mr-2" /> Ajouter l'annonce
          </button>
        </div>
      )}

      <h4 className="text-xl font-semibold text-purple-700 mb-3">Annonces existantes</h4>
      {announcements.length === 0 && !loading && (
        <p className="text-gray-600">Aucune annonce trouvée.</p>
      )}
      <div className="space-y-4">
        {announcements.map((annonce) => (
          <div key={annonce.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            {editingAnnouncement?.id === annonce.id ? (
              <div>
                <input
                  type="text"
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md mb-2 focus:ring-purple-500 focus:border-purple-500 font-semibold text-lg"
                />
                <textarea
                  value={editingContent}
                  onChange={(e) => setEditingContent(e.target.value)}
                  rows="4"
                  className="w-full p-2 border border-gray-300 rounded-md mb-3 focus:ring-purple-500 focus:border-purple-500"
                ></textarea>
                <div className="flex space-x-2">
                  <button
                    onClick={handleUpdateAnnouncement}
                    className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition duration-200 flex items-center"
                    disabled={loading}
                  >
                    <CheckCircle size={18} className="mr-2" /> Enregistrer
                  </button>
                  <button
                    onClick={() => setEditingAnnouncement(null)}
                    className="bg-gray-400 text-white py-2 px-4 rounded-md hover:bg-gray-500 transition duration-200 flex items-center"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h5 className="font-bold text-xl text-gray-800 mb-1">{annonce.title}</h5>
                <p className="text-gray-700 mb-2">{annonce.content}</p>
                <p className="text-sm text-gray-500">
                  Publié le : {annonce.createdAt}
                  {annonce.schoolId && ` (École: ${annonce.schoolId})`}
                </p>
                {(isAdminOrHigher || userRole === 'gestionnaire') && (
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={() => handleEditClick(annonce)}
                      className="text-blue-600 hover:text-blue-800 transition duration-200 flex items-center"
                    >
                      <Edit size={18} className="mr-1" /> Modifier
                    </button>
                    <button
                      onClick={() => handleDeleteAnnouncement(annonce.id)}
                      className="text-red-600 hover:text-red-800 transition duration-200 flex items-center"
                      disabled={loading}
                    >
                      <Trash2 size={18} className="mr-1" /> Supprimer
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnnoncesManagement;